<template>
  <div class="group bg-white rounded-[24px] p-4 border border-[#1c4848]/5 shadow-sm hover:shadow-xl hover:border-[#1c4848]/10 transition-all duration-300 relative">
    <div class="flex gap-4">
      <div class="w-24 h-32 rounded-2xl overflow-hidden relative shrink-0 shadow-md">
        <img
          :src="coverUrl"
          :alt="item.title"
          class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        >
        <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
        <button
          type="button"
          class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          @click="$emit('play', item)"
        >
          <div class="w-10 h-10 rounded-full bg-white/90 text-[#1c4848] flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </button>
      </div>

      <div class="flex-1 flex flex-col min-w-0">
        <div class="flex justify-between items-start mb-1 flex-wrap gap-1">
          <span
            class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border"
            :class="typeBadgeClass"
          >
            {{ typeLabel }}
          </span>
          <span v-if="audienceLabel" class="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#e0f2fe] text-[#0369a1] border border-[#0ea5e9]/20">
            {{ ageLabel ? `${audienceLabel} · ${ageLabel}` : audienceLabel }}
          </span>
          <div class="flex text-yellow-400 text-[10px] ml-auto">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            <span class="text-[#1c4848]/40 ml-1 font-bold">{{ ratingDisplay }}</span>
          </div>
        </div>

        <h3 class="font-serif text-lg font-bold text-[#1c4848] leading-tight mb-1 truncate">{{ item.title }}</h3>
        <p class="text-xs text-[#1c4848]/50 line-clamp-2 mb-3 leading-relaxed">
          {{ item.description || '—' }}
        </p>

        <div class="mt-auto flex items-center justify-between border-t border-[#1c4848]/5 pt-3">
          <div class="flex items-center gap-2">
            <div v-if="authorAvatar" class="w-6 h-6 rounded-full bg-gray-100 overflow-hidden">
              <img :src="authorAvatar" :alt="authorName" class="w-full h-full object-cover">
            </div>
            <div v-else class="w-6 h-6 rounded-full bg-[#1c4848] text-white flex items-center justify-center text-[10px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
            </div>
            <span class="text-[11px] font-bold text-[#1c4848]/70">{{ authorName }}</span>
          </div>
          <span class="text-[10px] font-bold text-[#1c4848]/40 bg-gray-50 px-2 py-1 rounded-md">
            {{ durationDisplay }}
          </span>
        </div>
      </div>
    </div>

    <div class="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        class="w-8 h-8 rounded-full bg-white border border-[#1c4848]/10 text-emerald-600 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors shadow-sm"
        title="Publish to feed"
        @click="publishToFeed"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
      </button>
      <button
        type="button"
        class="w-8 h-8 rounded-full bg-white border border-[#1c4848]/10 text-[#1c4848] flex items-center justify-center hover:bg-[#1c4848] hover:text-white transition-colors shadow-sm"
        @click="$emit('edit', item)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      </button>
      <button
        type="button"
        class="w-8 h-8 rounded-full bg-white border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors shadow-sm"
        title="Delete"
        @click="$emit('delete', item)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useContentFeedStore } from '@/store/content-feed';
import { useAudioContentsStore } from '@/store/audio-contents';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';

const props = defineProps({
  item: { type: Object, required: true },
});

defineEmits(['play', 'edit', 'delete']);

const contentFeedStore = useContentFeedStore();
const audioStore = useAudioContentsStore();

function publishToFeed() {
  const item = props.item;
  if (!item?.id) return;
  if (!confirm('Publish to feed?')) return;
  const title = item?.title ?? 'Audio';
  const type = getContentFeedType(item, 'audio');
  const languageId = getContentLanguageId(item);
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((res) => {
    audioStore.attachContentFeed(item.id, res.id);
  });
}

const TYPE_MAP = {
  story: { label: 'Story', class: 'bg-[#ecd79f]/20 text-[#8c7335] border-[#ecd79f]/20' },
  nasheed: { label: 'Nasheed', class: 'bg-[#e1f5e8] text-[#00695c] border-[#00695c]/10' },
  calm_sound: { label: 'Calm Sound', class: 'bg-[#f3f4f6] text-[#4b5563] border-gray-200' },
};

const coverUrl = computed(() => props.item.cover?.url || 'https://placehold.co/96x128/153838/ecd79f?text=Cover');
const typeLabel = computed(() => TYPE_MAP[props.item.type]?.label || props.item.type);
const typeBadgeClass = computed(() => TYPE_MAP[props.item.type]?.class || 'bg-gray-100 text-gray-600 border-gray-200');
const ratingDisplay = computed(() => (props.item.rating != null ? Number(props.item.rating).toFixed(1) : '—'));
const authorName = computed(() => props.item.author?.nickname || props.item.author?.name || 'System');
const authorAvatar = computed(() => null);
const audienceLabel = computed(() => (props.item.audience === 'kids' ? 'Kids' : props.item.audience === 'adults' ? 'Adults' : null));
const ageLabel = computed(() => {
  if (props.item.audience !== 'kids' || props.item.age_from == null) return null;
  if (props.item.age_to == null) return `${props.item.age_from}+`;
  return `${props.item.age_from}–${props.item.age_to}`;
});
const durationDisplay = computed(() => {
  const d = props.item.duration;
  if (d == null || d === 0) return 'Loop';
  const m = Math.floor(d / 60);
  const s = d % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
});
</script>
